
import { Injectable } from '@angular/core';
import { AuthChangeEvent, RealtimeChannel, RealtimePostgresChangesPayload, Session, SupabaseClient, User } from '@supabase/supabase-js';
import { ApplicationDetail, ApplicationDocument, ApplicationLog, OnboardingApplication, SystemDetails } from '../models/application';
import { ContactDetailsUpdate } from '../models/contact-details-update.model';
import { AppUser } from '../models/user';
import { environment } from '../environments/environment';

declare global {
  interface Window {
    supabase: {
      createClient: (url: string, key: string) => SupabaseClient;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class SupabaseService {
  private supabase: SupabaseClient;

  constructor() {
    if (window.supabase) {
      console.log('[SupabaseService] Initializing Supabase client...');
      this.supabase = window.supabase.createClient(environment.supabaseUrl, environment.supabaseKey);
      console.log('[SupabaseService] Supabase client initialized.');
    } else {
      console.error('[SupabaseService] Supabase client not found on window. Make sure the script is loaded.');
    }
  }

  // --- Auth Methods ---

  getSession() {
    return this.supabase.auth.getSession();
  }

  authStateChanges(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    const { data: { subscription } } = this.supabase.auth.onAuthStateChange(callback);
    return subscription;
  }

  async signInWithEmailOtp(email: string) {
    console.log('[SupabaseService] signInWithEmailOtp called for:', email);
    const { error } = await this.supabase.auth.signInWithOtp({
      email,
      options: {
        // This site is for existing reviewers only. New users must be invited by an admin.
        shouldCreateUser: false,
        emailRedirectTo: environment.appUrl,
      },
    });
    if (error) {
      console.error('[SupabaseService] Error sending OTP code:', error);
      throw error;
    }
    console.log('[SupabaseService] OTP code sent successfully.');
  }

  async verifyEmailOtp(email: string, token: string) {
    console.log('[SupabaseService] verifyEmailOtp called.');
    const trimmedToken = token.trim();

    // Since this app does not support new user registration via OTP, we only expect
    // 'magiclink' tokens for existing users who are signing in.
    const { data, error } = await this.supabase.auth.verifyOtp({
        email,
        token: trimmedToken,
        type: 'magiclink',
    });
    
    // If there's an error, throw it to be handled by the component.
    if (error) {
        console.error('[SupabaseService] Magic link verification failed:', error);
        throw error;
    }

    if (!data || !data.session) {
      console.error('[SupabaseService] OTP verification succeeded but returned no session.');
      throw new Error('Verification failed: No session returned.');
    }
    
    console.log('[SupabaseService] OTP verified successfully.');
    return data;
  }

  async signOut() {
    console.log('[SupabaseService] signOut called.');
    const { error } = await this.supabase.auth.signOut();
    if (error) {
      console.error('[SupabaseService] Error signing out:', error.message);
      throw error;
    }
    console.log('[SupabaseService] signOut successful.');
  }
  
  async inviteReviewer(email: string): Promise<void> {
    console.log(`[SupabaseService] Invoking 'invite-reviewer' edge function for ${email}`);

    const { data: { session } } = await this.supabase.auth.getSession();
    if (!session) {
      throw new Error('Authentication error: No active session found.');
    }

    const { error } = await this.supabase.functions.invoke('invite-reviewer', {
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': environment.supabaseKey,
            'Content-Type': 'application/json'
        },
        body: { email },
    });

    if (error) {
        console.error('Error inviting reviewer via edge function:', error);
        throw error;
    }
    console.log(`[SupabaseService] Invitation function invoked for ${email}.`);
  }

  // --- User Management Methods ---
  async getUsers(): Promise<AppUser[]> {
    const { data, error } = await this.supabase.rpc('get_all_users');
    if (error) {
      console.error('Error fetching users via RPC:', error.message, 'Details:', error);
      throw new Error(`Error fetching users via RPC: ${error.message}`);
    }
    return data as AppUser[];
  }

  async updateUserRole(userId: string, isReviewer: boolean): Promise<void> {
    const { error } = await this.supabase.rpc('set_reviewer_role', {
      target_user_id: userId,
      is_reviewer: isReviewer,
    });
    if (error) {
      console.error(`Error updating role for user ${userId}:`, error.message, 'Details:', error);
      throw new Error(`Error updating role: ${error.message}`);
    }
  }

  // --- Application Data Methods ---

  async getApplications(): Promise<OnboardingApplication[]> {
    const { data, error } = await this.supabase
      .from('onboarding_applications')
      .select('id, created_at, updated_at, user_id, status, submitted_at, current_step, onboarding_application_data(*), onboarding_webhook_queue(*)');

    if (error) {
      console.error(`Error fetching applications: ${error.message}`, error);
      return [];
    }
    
    console.log('[SupabaseService] Raw application data from Supabase:', JSON.stringify(data, null, 2));

    return data as OnboardingApplication[];
  }

  async getApplicationDetails(id: string): Promise<ApplicationDetail | null> {
    const applicationPromise = this.supabase
        .from('onboarding_applications')
        .select('*')
        .eq('id', id)
        .single();
    
    const dataPromise = this.supabase
        .from('onboarding_application_data')
        .select('*')
        .eq('application_id', id);

    const documentsPromise = this.supabase
        .from('onboarding_documents')
        .select('*')
        .eq('application_id', id);

    const logsPromise = this.supabase
        .from('onboarding_application_log')
        .select('*')
        .eq('application_id', id)
        .order('created_at', { ascending: false });

    const webhookPromise = this.supabase
        .from('onboarding_webhook_queue')
        .select('*')
        .eq('application_id', id)
        .order('created_at', { ascending: false });

    const [appResult, dataResult, documentsResult, logsResult, webhookResult] = await Promise.all([
        applicationPromise,
        dataPromise,
        documentsPromise,
        logsPromise,
        webhookPromise
    ]);

    if (appResult.error) {
        console.error(`Error fetching application details: ${appResult.error.message}`, appResult.error);
        return null;
    }
    if (dataResult.error) {
        console.error(`Error fetching application data: ${dataResult.error.message}`, dataResult.error);
        return null;
    }
    if (documentsResult.error) {
        console.error(`Error fetching application documents: ${documentsResult.error.message}`, documentsResult.error);
        return null;
    }
    if (logsResult.error) {
        console.error(`Error fetching application logs: ${logsResult.error.message}`, logsResult.error);
        return null;
    }
    if (webhookResult.error) {
        console.error(`Error fetching application webhooks: ${webhookResult.error.message}`, webhookResult.error);
        return null;
    }

    // Fetch system details if application has been approved
    let systemDetails = null;
    if (appResult.data.system_details_id) {
        const systemDetailsResult = await this.supabase
            .from('system_details')
            .select('*')
            .eq('id', appResult.data.system_details_id)
            .single();

        if (systemDetailsResult.error) {
            console.error(`Error fetching system details: ${systemDetailsResult.error.message}`, systemDetailsResult.error);
        } else {
            systemDetails = systemDetailsResult.data;
        }
    }

    return {
        ...appResult.data,
        data: dataResult.data || [],
        documents: documentsResult.data || [],
        logs: logsResult.data || [],
        webhooks: webhookResult.data || [],
        system_details: systemDetails
    } as ApplicationDetail;
  }

  async updateApplicationStatus(id: string, status: OnboardingApplication['status']): Promise<boolean> {
      const { error } = await this.supabase
        .from('onboarding_applications')
        .update({ status })
        .eq('id', id);

    if (error) {
        console.error(`Error updating status: ${error.message}`, error);
        return false;
    }
    return true;
  }

  async updateApplicationDecision(id: string, decision: 'rejected', notes: string): Promise<boolean> {
    const updatePayload = {
      status: decision,
      decision: decision,
      decision_notes: notes,
      decision_at: new Date().toISOString()
    };
    const { error } = await this.supabase
      .from('onboarding_applications')
      .update(updatePayload)
      .eq('id', id);

    if (error) {
        console.error(`Error updating decision: ${error.message}`, error);
        return false;
    }
    return true;
  }

  async deleteApplication(applicationId: string): Promise<void> {
    // Step 1: Collect storage paths from recorded documents + any orphaned files
    // under the application's prefix in the bucket.
    const { data: docs } = await this.supabase
      .from('onboarding_documents')
      .select('storage_path')
      .eq('application_id', applicationId);

    const recordedPaths = (docs ?? []).map(d => d.storage_path as string).filter(Boolean);

    const { data: listed } = await this.supabase.storage
      .from('onboarding-documents')
      .list(applicationId);

    const listedPaths = (listed ?? []).map(f => `${applicationId}/${f.name}`);

    const allPaths = [...new Set([...recordedPaths, ...listedPaths])];

    // Step 2: Delete storage files via the Storage API (direct SQL is not permitted)
    if (allPaths.length > 0) {
      const { error: storageError } = await this.supabase.storage
        .from('onboarding-documents')
        .remove(allPaths);

      if (storageError) {
        console.warn('Storage deletion partially failed:', storageError.message);
      }
    }

    // Step 3: Delete DB records (webhook queue + application row, cascades child tables)
    const { error } = await this.supabase.rpc('delete_application', {
      p_application_id: applicationId
    });

    if (error) {
      console.error('Error deleting application:', error.message);
      throw new Error(error.message);
    }
  }

  async revertApprovedToSubmitted(applicationId: string): Promise<void> {
    const { error } = await this.supabase.rpc('revert_approved_to_submitted', {
      p_application_id: applicationId
    });

    if (error) {
      console.error('Error reverting approved application:', error.message);
      throw new Error(`Failed to revert application: ${error.message}`);
    }
  }

  async reopenApplication(id: string, newStatus: 'submitted' | 'in_review'): Promise<boolean> {
    const { error } = await this.supabase
      .from('onboarding_applications')
      .update({ status: newStatus, decision: null, decision_notes: null, decision_at: null })
      .eq('id', id);

    if (error) {
      console.error(`Error reopening application: ${error.message}`, error);
      return false;
    }
    return true;
  }

  async approveApplicationWithSystemDetails(
    applicationId: string,
    notes: string,
    logonDomain: string,
    accountShortName: string
  ): Promise<boolean> {
    // Use the RPC function which handles everything in a single transaction
    const { error } = await this.supabase.rpc('approve_application_with_system_details', {
      p_application_id: applicationId,
      p_notes: notes,
      p_logon_domain: logonDomain,
      p_account_short_name: accountShortName
    });

    if (error) {
        console.error('Error approving application with system details:', error.message);
        // Check for unique constraint violation on logon_domain or account_short_name
        if (error.code === '23505') {
             throw new Error('A record with the provided Logon Domain or Account Short Name already exists. Please use different values.');
        }
        throw new Error(`Failed to approve application: ${error.message}`);
    }

    return true;
  }

  async getSystemDetails(excludeId?: string): Promise<Pick<SystemDetails, 'logon_domain' | 'account_short_name'>[]> {
    let query = this.supabase
        .from('system_details')
        .select('logon_domain, account_short_name');

    if (excludeId) {
        query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) {
        console.error('Error fetching system details:', error.message);
        throw error;
    }
    return data || [];
  }

  async addApplicationLog(log: Omit<ApplicationLog, 'id' | 'created_at'>): Promise<boolean> {
      const { error } = await this.supabase
        .from('onboarding_application_log')
        .insert([log]);

    if (error) {
        console.error(`Error adding log: ${error.message}`, error);
        return false;
    }
    return true;
  }

  async createSignedUrl(document: ApplicationDocument, expiresIn = 3600): Promise<{ signedUrl: string } | null> {
    if (!document.storage_path || !document.storage_bucket) {
      console.error('[SupabaseService] createSignedUrl failed: Document object is missing `storage_path` or `storage_bucket`.', document);
      return null;
    }

    const { data, error } = await this.supabase
      .storage
      .from(document.storage_bucket)
      .createSignedUrl(document.storage_path, expiresIn);
      
    if (error) {
      console.error(`[SupabaseService] Error creating signed URL for document '${document.id}':`, error.message);
      return null;
    }

    if (!data || !data.signedUrl) {
      console.error('[SupabaseService] Supabase client failed to construct a signed URL.', { document });
      return null;
    }

    console.log(`[SupabaseService] Generated signed URL for document '${document.id}'.`);
      
    return data;
  }

  async updateApplicationDocument(documentId: string, payload: { expiry_date?: string; storage_path?: string }): Promise<boolean> {
    const { error } = await this.supabase
      .from('onboarding_documents')
      .update(payload)
      .eq('id', documentId);

    if (error) {
      console.error(`Error updating document ${documentId}: ${error.message}`, error);
      return false;
    }
    return true;
  }

  async saveCroppedDocument(document: ApplicationDocument, blob: Blob): Promise<boolean> {
    const newPath = `${document.application_id}/${document.document_type}/cropped-${crypto.randomUUID()}.png`;

    const { error: uploadError } = await this.supabase
      .storage
      .from('onboarding-documents')
      .upload(newPath, blob, { contentType: 'image/png' });

    if (uploadError) {
      console.error(`[SupabaseService] Error uploading cropped document:`, uploadError.message);
      return false;
    }

    const updateSuccess = await this.updateApplicationDocument(document.id, { storage_path: newPath });
    if (!updateSuccess) {
      console.error(`[SupabaseService] Uploaded file but failed to update document record.`);
      return false;
    }

    // Delete the old file from storage — best-effort, don't fail the save.
    const oldPath = document.storage_path;
    if (oldPath && oldPath !== newPath) {
      const { error: deleteError } = await this.supabase
        .storage
        .from('onboarding-documents')
        .remove([oldPath]);
      if (deleteError) {
        console.warn(`[SupabaseService] Could not delete old file at ${oldPath}:`, deleteError.message);
      }
    }

    console.log(`[SupabaseService] Cropped document saved at path: ${newPath}`);
    return true;
  }

  async updateApplicationContactDetails(applicationId: string, data: ContactDetailsUpdate): Promise<void> {
    const { error } = await this.supabase.rpc('update_application_contact_details', {
      p_application_id:   applicationId,
      p_fleet_name:       data.fleet_name,
      p_address_line1:    data.address_line1,
      p_address_line2:    data.address_line2,
      p_address_city:     data.address_city,
      p_address_county:   data.address_county,
      p_address_postcode: data.address_postcode,
      p_latitude:         data.latitude,
      p_longitude:        data.longitude,
      p_contact_name:     data.contact_name,
      p_contact_email:    data.contact_email,
      p_contact_phone:    data.contact_phone,
      p_fleet_phone:      data.fleet_phone,
    });

    if (error) {
      console.error('[SupabaseService] updateApplicationContactDetails failed:', error.message);
      throw new Error(error.message);
    }
  }

  async sendLegalDocuments(applicationId: string): Promise<void> {
    const { data: { session } } = await this.supabase.auth.getSession();
    if (!session) {
      throw new Error('Authentication error: No active session found.');
    }

    const { data, error } = await this.supabase.functions.invoke('send-legal-documents', {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': environment.supabaseKey,
        'Content-Type': 'application/json',
      },
      body: { applicationId },
    });

    if (error) {
      console.error('[SupabaseService] sendLegalDocuments failed:', error);
      let message = error.message;
      try {
        const body = await (error as any).context?.json?.();
        if (body?.error) message = body.error;
      } catch { /* response body unreadable; use default message */ }
      throw new Error(message);
    }

    console.log('[SupabaseService] Legal documents email sent for application', applicationId);
  }

  // --- Realtime Methods ---

  subscribeToTableChanges(
    table: string,
    schema: string,
    channelName: string,
    callback: (payload: RealtimePostgresChangesPayload<{ [key: string]: any }>) => void
  ): RealtimeChannel {
    const channel = this.supabase.channel(channelName)
      .on('postgres_changes', { event: '*', schema, table }, (payload) => {
        console.log(`[SupabaseService] Change received on ${channelName}`, payload);
        callback(payload);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`[SupabaseService] Successfully subscribed to channel: ${channelName}`);
        } else {
          console.warn(`[SupabaseService] Subscription status for ${channelName}: ${status}`);
        }
      });
    return channel;
  }

  unsubscribeFromChannel(channel: RealtimeChannel) {
    if (channel) {
      console.log(`[SupabaseService] Unsubscribing from channel ${channel.topic}`);
      this.supabase.removeChannel(channel);
    }
  }
}
